import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import logger from '../../utils/logger';

const UserLogin = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    logger?.debug('VALIDATION', 'Starting form validation');
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'L\'email est requis';
      logger?.validationError('email', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Format d\'email invalide';
      logger?.validationError('email', 'Invalid email format');
    }

    if (!formData?.password) {
      newErrors.password = 'Le mot de passe est requis';
      logger?.validationError('password', 'Password is required');
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
      logger?.validationError('password', 'Password too short');
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors)?.length === 0;
    logger?.info('VALIDATION', `Form validation ${isValid ? 'passed' : 'failed'}`, { 
      errorCount: Object.keys(newErrors)?.length,
      errors: Object.keys(newErrors)
    });
    return isValid;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    logger?.info('AUTH', '=== LOGIN FORM SUBMISSION STARTED ===');
    logger?.debug('AUTH', 'Form data', { email: formData?.email });
    
    if (!validateForm()) {
      logger?.warn('AUTH', 'Form validation failed, aborting login');
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    logger?.info('AUTH', 'Form validated, calling signIn method');

    try {
      const loginStartTime = performance.now();
      logger?.debug('AUTH', 'Invoking signIn from AuthContext', { email: formData?.email });
      
      const { data, error } = await signIn(formData?.email, formData?.password);
      
      const loginDuration = performance.now() - loginStartTime;
      logger?.info('AUTH', `Login attempt completed in ${loginDuration?.toFixed(2)}ms`);

      if (error) {
        logger?.error('AUTH', 'Login returned error', {
          errorMessage: error?.message,
          errorCode: error?.code,
          errorStatus: error?.status
        });
        
        if (error?.message?.includes('Invalid login credentials')) {
          logger?.warn('AUTH', 'Invalid credentials provided');
          setErrors({ submit: 'Email ou mot de passe incorrect' });
        } else if (error?.message?.includes('Email not confirmed')) {
          logger?.warn('AUTH', 'Email not confirmed');
          setErrors({ submit: 'Veuillez confirmer votre email avant de vous connecter' });
        } else {
          logger?.error('AUTH', 'Unknown login error', { error });
          setErrors({ submit: error?.message || 'Erreur lors de la connexion' });
        }
        setIsSubmitting(false);
        logger?.info('AUTH', '=== LOGIN FAILED ===');
        return;
      }

      // Get user role from the auth response
      const userRole = data?.user?.user_metadata?.role || 'student';
      logger?.info('AUTH', 'Login successful, preparing redirect', {
        userId: data?.user?.id,
        email: data?.user?.email,
        role: userRole
      });

      // Redirect based on role
      let redirectPath;
      switch (userRole) {
        case 'parent': 
          redirectPath = '/parent-dashboard-interface';
          break;
        case 'counselor': 
          redirectPath = '/counselor-management-portal';
          break;
        case 'student':
        default:
          redirectPath = '/student-dashboard';
          break;
      }
      
      logger?.info('AUTH', `Redirecting to ${redirectPath}`);
      logger?.info('AUTH', '=== LOGIN SUCCESSFUL ===');
      navigate(redirectPath);
    } catch (error) {
      logger?.error('AUTH', 'Unexpected error during login', {
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
        isTypeError: error instanceof TypeError
      });
      logger?.networkError('Login form submission', error);
      console.error('Login error:', error);
      setErrors({ submit: 'Erreur réseau. Veuillez réessayer.' });
      setIsSubmitting(false);
      logger?.info('AUTH', '=== LOGIN FAILED WITH EXCEPTION ===');
    }
  };

  const handleSocialLogin = (provider) => {
    // Placeholder for social login implementation
    console.log(`Social login with ${provider}`);
    setErrors({ submit: `Connexion ${provider} sera bientôt disponible` });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Left Panel - Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 border border-gray-100">
          <div className="mb-8">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                <Icon name="GraduationCap" size={24} color="#FFFFFF" />
              </div>
              <span className="text-xl font-bold text-gray-900">CURSUS Orientation</span>
            </Link>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Bon Retour !
            </h1>
            <p className="text-gray-600">
              Connectez-vous pour accéder à votre espace personnalisé
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {errors?.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertCircle" size={20} color="#DC2626" />
                <p className="text-sm text-red-600 flex-1">{errors?.submit}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Adresse Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData?.email}
                onChange={handleInputChange}
                placeholder="exemple@email.com"
                className={errors?.email ? 'border-red-500' : ''}
              />
              {errors?.email && (
                <p className="mt-1 text-sm text-red-600">{errors?.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Mot de Passe
              </label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData?.password}
                  onChange={handleInputChange}
                  placeholder="Entrez votre mot de passe"
                  className={errors?.password ? 'border-red-500' : ''}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
              {errors?.password && (
                <p className="mt-1 text-sm text-red-600">{errors?.password}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e?.target?.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Se souvenir de moi</span>
              </label>

              <Link
                to="#"
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Mot de passe oublié ?
              </Link>
            </div>

            <Button
              type="submit"
              variant="default"
              size="lg"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Icon name="Loader" size={20} className="animate-spin" />
                  <span>Connexion en cours...</span>
                </>
              ) : (
                <>
                  <Icon name="LogIn" size={20} />
                  <span>Se Connecter</span>
                </>
              )}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Ou continuer avec</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleSocialLogin('Google')}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-300"
              >
                <Icon name="Chrome" size={20} />
                <span className="text-sm font-medium text-gray-700">Google</span>
              </button>

              <button
                type="button"
                onClick={() => handleSocialLogin('Facebook')}
                className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-300"
              >
                <Icon name="Facebook" size={20} />
                <span className="text-sm font-medium text-gray-700">Facebook</span>
              </button>
            </div>
          </div>

          <p className="mt-8 text-center text-sm text-gray-600">
            Vous n'avez pas de compte ?{' '}
            <Link
              to="/registration-role-selection"
              className="font-medium text-blue-600 hover:text-blue-700"
            >
              Inscrivez-vous maintenant
            </Link>
          </p>
        </div>

        {/* Right Panel - Benefits */}
        <div className="hidden lg:block">
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Accédez à Votre Parcours Personnalisé
              </h2>
              <p className="text-lg text-gray-600">
                Connectez-vous pour continuer votre exploration et découvrir les opportunités qui vous correspondent.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} color="#2563EB" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Suivi de Progression
                  </h3>
                  <p className="text-sm text-gray-600">
                    Visualisez vos progrès et vos accomplissements en temps réel
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="Award" size={24} color="#10B981" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Recommandations Personnalisées
                  </h3>
                  <p className="text-sm text-gray-600">
                    Recevez des suggestions adaptées à votre profil unique
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Icon name="MessageSquare" size={24} color="#F59E0B" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Assistant IA Disponible 24/7
                  </h3>
                  <p className="text-sm text-gray-600">
                    Posez vos questions à tout moment et obtenez des réponses instantanées
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} color="#8B5CF6" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Communauté et Support
                  </h3>
                  <p className="text-sm text-gray-600">
                    Connectez-vous avec des conseillers et d'autres étudiants
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-6 text-white">
              <div className="flex items-center gap-3 mb-3">
                <Icon name="Sparkles" size={24} color="#FFFFFF" />
                <h3 className="font-bold text-lg">Nouveauté</h3>
              </div>
              <p className="text-blue-100">
                Découvrez notre nouveau système de quiz interactif avec des recommandations encore plus précises basées sur l'IA !
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLogin;